import type { ReactNode } from "react";
import type { SimpleColumn } from "ui-kit/src/Datagrid/DatagridV2";
import type { GameRow } from "../../../../datagrid-configs/discoverGames";
import { DISCOVER_GAMES_DATA_COLUMN_FALLBACK, DISCOVER_GAMES_DEFAULT_COLUMN_GROUP } from "../../../../datagrid-configs/discoverGames/constants";
import type { ColumnConfig, SimpleColumnBuildContext, SortConfig } from "../../../../datagrid-configs/types";
import type { GroupColors } from "../../../../datagrid/ColumnVisibilityModal";
import { createSortArrows } from "features/discover/functions";
import { _ } from "i18n";
import { collectPageRowIds } from "./collectPageRowIds";
import { GroupColorCellWrap, GroupColorHeaderWrap, resolveGroupColors } from "./groupColorGrading";
import { orderColumnsForDisplay } from "./orderColumnsForDisplay";

export interface BuildSimpleColumnsArgs {
	discoverGamesColumns: ColumnConfig<GameRow>[];
	columnOrder: string[];
	columnWidths: Record<string, number>;
	groupColors: GroupColors;
	items: GameRow[];
	sort: SortConfig | undefined;
	handleSort: (newSort?: SortConfig) => void;
}

/** Convert ColumnConfig to SimpleColumn format for DatagridV2 (respecting order). */
export function buildSimpleColumns(args: BuildSimpleColumnsArgs): SimpleColumn<GameRow>[] {
	const { discoverGamesColumns, columnOrder, columnWidths, groupColors, items, sort, handleSort } = args;

	const orderedColumns = orderColumnsForDisplay(discoverGamesColumns, columnOrder);
	const pageRowIds = collectPageRowIds(items);
	const emptyCellValue = _("discover.gamesTable.cellValue.none");

	return orderedColumns.map((col) => {
		const colKey = String(col.key);
		const buildContext: SimpleColumnBuildContext = { pageRowIds, sort, handleSort };
		const { groupBgColor, groupTextColor } = resolveGroupColors(
			groupColors,
			col.group || DISCOVER_GAMES_DEFAULT_COLUMN_GROUP,
		);

		const defaultMin = DISCOVER_GAMES_DATA_COLUMN_FALLBACK.min;
		const minWidth = col.minWidth ?? col.width ?? defaultMin;
		const width = columnWidths[colKey];

		const wrapCell = (content: ReactNode) => {
			return (
				<GroupColorCellWrap groupBgColor={groupBgColor} groupTextColor={groupTextColor}>
					{content}
				</GroupColorCellWrap>
			);
		};

		const sortFieldForApi = col.sortField || colKey;
		const isCenteredHeader = colKey === "score" || colKey === "ageCategory";
		const usesGroupColorHeaderWrap = col.header !== undefined || col.sortable || groupBgColor || isCenteredHeader;
		let header: string | (() => ReactNode);

		if (usesGroupColorHeaderWrap) {
			const renderHeader = col.header;
			const centerClass = isCenteredHeader ? " discoverGamesTableV3-thCell--center" : "";
			const headerClassName = col.sortable ? `tableHeader${centerClass}` : centerClass.trim();

			header = () => {
				let headerContent: ReactNode;
				if (renderHeader) {
					headerContent = renderHeader(buildContext);
				} else if (col.sortable) {
					headerContent = (
						<>
							<span className="tableHeader-label">{col.label}</span>
							<span className="tableHeader-sortArrows">
								{createSortArrows(sortFieldForApi, sort, handleSort)}
							</span>
						</>
					);
				} else {
					headerContent = col.label;
				}

				return (
					<GroupColorHeaderWrap
						groupBgColor={groupBgColor}
						groupTextColor={groupTextColor}
						className={headerClassName}
					>
						{headerContent}
					</GroupColorHeaderWrap>
				);
			};
		} else {
			header = col.label;
		}

		let cell: SimpleColumn<GameRow>["cell"];
		if (col.cell) {
			const renderCell = col.cell;
			cell = (value, row) => wrapCell(renderCell(value, row));
		} else if (groupBgColor) {
			cell = (value) => wrapCell(value != null ? String(value) : emptyCellValue);
		}

		return {
			key: col.key,
			id: colKey,
			header,
			minWidth,
			width,
			...(col.maxWidth !== undefined ? { maxWidth: col.maxWidth } : {}),
			...(col.sticky ? { sticky: true } : {}),
			...(cell !== undefined ? { cell } : {}),
		};
	});
}
